import { TAppDispatch, TGetState } from 'store/types'
import slice from 'store/features/editor/slice'
import { editorSelectors } from 'store/features/editor/selectors'
import { SUB_SIDEBAR_KEYS } from 'consts/sidebar'
import { withAppThunks } from 'store/with-app-thunks'
import { selectionThunks } from 'store/features/editor/thunks/selection'
import { IndexedDB } from 'services/indexed-db'
import { ITableConfigIndexedDB } from 'services/indexed-db/types'
import {
  IMAGE_TO_VIDEO_AI_DATA_STORAGE_KEY,
  PROJECT_MEDIA_TYPES,
  UPGRADE_POPUP_OPENER_REF
} from 'consts'
import { aiVideoSelectors } from 'store/features/aiVideo/selectors'
import type { IUploadMediaItem } from 'types/media'
import { getLibraryFonts_API } from 'api'
import { ReferenceMediaImageType } from 'types/aiVideo'

const { actions } = slice

const LANDING_DB_NAME = 'image-to-video-ai-landing'
const LANDING_TABLE_NAME = 'image-to-video-ai-data'
const LANDING_TABLE_CONFIG: ITableConfigIndexedDB = {
  name: LANDING_TABLE_NAME
}

interface IImageToVideoAiLandingData {
  prompt?: string
  selectedStyleId?: number | null
  selectedAspectRatio?: string | null
  uploadedImage?: Blob | null
  uploadedImageName?: string | null
  uploadedImageType?: string | null
}

const setActiveSidebarTabAndCloseOverlay =
  (tabKey: string) => (dispatch: TAppDispatch, getState: TGetState) => {
    const activeOverlay = editorSelectors.activeAsideOverlayKey(getState())

    if (activeOverlay) {
      // Use toggle to close if same overlay is open
      dispatch(actions.toggleAsideOverlayContent(activeOverlay))
    } else {
      dispatch(actions.setAsideOverlayContent(null))
    }

    dispatch(actions.setActiveSidebarTab(tabKey))
  }

const getLibraryFonts = () => async (dispatch: TAppDispatch) => {
  const response = await getLibraryFonts_API()
  dispatch(actions.setLibraryFonts(response.data))
}

const setToDefaultTab = () =>
  withAppThunks(appThunks => async (dispatch: TAppDispatch) => {
    const formCleaningMap = {
      [SUB_SIDEBAR_KEYS.AI_IMAGE]: appThunks.aiImageThunks.resetGenerateForm,
      [SUB_SIDEBAR_KEYS.AI_VIDEO]: appThunks.aiVideoThunks.clearGenerationParams
    }
    const { sidebarItems } = await import('containers/Sidebar/consts')
    dispatch(actions.setActiveSidebarTab(sidebarItems[0].key))
    const formCleaningThunk = formCleaningMap[sidebarItems[0].key]
    if (formCleaningThunk) {
      dispatch(formCleaningThunk())
    }
  })

const applyImageToVideoAiLandingData = (isMobile: boolean) =>
  withAppThunks(
    appThunks => async (dispatch: TAppDispatch, getState: TGetState) => {
      const db = IndexedDB.getInstance(LANDING_DB_NAME, [LANDING_TABLE_CONFIG])
      const data = await db.readRecord<IImageToVideoAiLandingData>(
        LANDING_TABLE_NAME,
        IMAGE_TO_VIDEO_AI_DATA_STORAGE_KEY
      )

      if (!data) {
        return
      }

      if (data.prompt) {
        dispatch(appThunks.aiVideoThunks.setPrompt(data.prompt))
      }
      if (!aiVideoSelectors.hasVideoModels(getState())) {
        await dispatch(appThunks.aiVideoThunks.getAIVideoModels())
      }

      if (data.selectedAspectRatio) {
        const state = getState()
        const currentModel = aiVideoSelectors.selectedModelCode(state)
        if (currentModel) {
          dispatch(
            appThunks.aiVideoThunks.updateSelectedModel({
              modelName: currentModel,
              aspectRatio: data.selectedAspectRatio
            })
          )
        }
      }

      if (data.uploadedImage && data.uploadedImage instanceof Blob) {
        const file = new File(
          [data.uploadedImage],
          data.uploadedImageName ?? 'uploaded-image.png',
          { type: data.uploadedImageType ?? 'image/png' }
        )
        const uploadItem: IUploadMediaItem = {
          id: `landing-${Date.now()}`,
          file,
          type: PROJECT_MEDIA_TYPES.image
        }
        await dispatch(
          appThunks.aiVideoThunks.uploadFrameImage(
            ReferenceMediaImageType.FIRST_FRAME,
            [uploadItem]
          )
        )
      }

      if (data.selectedStyleId !== null) {
        dispatch(
          appThunks.aiVideoThunks.setRefImageSelectedStyleId(
            data.selectedStyleId
          )
        )
      }

      await db.deleteRecord(
        LANDING_TABLE_NAME,
        IMAGE_TO_VIDEO_AI_DATA_STORAGE_KEY
      )

      dispatch(
        appThunks.editorThunks.setActiveSidebarTab(SUB_SIDEBAR_KEYS.AI_VIDEO)
      )
      if (isMobile) {
        dispatch(appThunks.editorThunks.setIsMobileSubSidebarOpen(true))
      }
    }
  )

const openUpgradePopup = (type: string) => () => {
  window.openUpgradePopup({
    type,
    openerRef: UPGRADE_POPUP_OPENER_REF
  })
}

export const editorThunks = {
  ...actions,
  ...selectionThunks,
  openUpgradePopup,
  setActiveSidebarTabAndCloseOverlay,
  setToDefaultTab,
  applyImageToVideoAiLandingData,
  getLibraryFonts
}
